/**
 * FieldTrackingTab (M3) — every unit currently in the field with
 * chain-of-custody stepper, upstream Procurement context and Cognara
 * right-sticky panel.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import ScanCenterDialog from '@/components/mtx/ScanCenterDialog';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Radar, RefreshCw, ChevronDown, ChevronRight, MapPin, List, Map as MapIcon,
         AlertTriangle, Sparkles, X, Loader2, FileText, Check, Package, ScanLine,
         Route, Timer, Flame, Users, Ruler, WifiOff, ArrowRight } from 'lucide-react';

const C = { primary: '#1E4E8C', accent: '#F26B1F', slate: '#0F172A',
             slateMuted: '#64748B', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444', info: '#3B82F6' };

const RISK_COLORS = { HIGH: C.danger, MEDIUM: C.warn, LOW: C.success };

const INTERFACE_TYPES = [
  'IQC_DESK', 'BIN_PUTAWAY', 'RESERVATION', 'ISSUE_COUNTER',
  'WH_EXIT_GATE', 'SITE_ENTRY_GATE', 'POINT_OF_USE', 'INSTALLATION',
  'RETURN_PICKUP', 'SITE_EXIT_GATE', 'WH_ENTRY_GATE', 'RECEIPT_COUNTER',
];

function fmtNum(n) { return Number(n || 0).toLocaleString(); }
function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toISOString().slice(0, 16).replace('T', ' ');
}

function KpiTile({ label, value, subValue, color, testId }) {
  return (
    <div className="p-2.5 rounded-lg border bg-white" style={{ borderColor: C.border }}
      data-testid={testId}>
      <div className="text-[9px] uppercase font-semibold tracking-wider" style={{ color: C.slateMuted }}>{label}</div>
      <div className="text-xl font-bold font-mono mt-0.5" style={{ color }}>{fmtNum(value)}</div>
      {subValue && (
        <div className="text-[9px] font-mono" style={{ color: C.slateMuted }}>{subValue}</div>
      )}
    </div>
  );
}

function fmtValue(v, currency) {
  if (v == null || isNaN(v)) return '—';
  const n = Number(v);
  const short = n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : n.toFixed(0);
  return `${currency || 'USD'} ${short}`;
}

function ChainOfCustodyDrawer({ projectId, row, onClose, onScanFromDrawer }) {
  const [stepper, setStepper] = useState([]);
  const [upstream, setUpstream] = useState(null);
  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandUpstream, setExpandUpstream] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [coc, up, tr] = await Promise.all([
        api.get(`/v1/projects/${projectId}/mtx/chain-of-custody/${row.materialUnitId}`),
        api.get(`/v1/projects/${projectId}/mtx/field-tracking/upstream/${row.materialUnitId}`),
        api.get(`/v1/projects/${projectId}/mtx/field-tracking/scan-trail/${row.materialUnitId}`),
      ]);
      setStepper(coc.data.stepper || []);
      setUpstream(up.data);
      setTrail(tr.data);
      setRefreshedAt(new Date());
    } catch { toast.error('Failed to load chain of custody'); }
    setLoading(false);
  }, [projectId, row.materialUnitId]);

  useEffect(() => {
    let alive = true;
    reload();
    // auto-refresh every 30s while drawer open
    const t = setInterval(() => { if (alive) reload(); }, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [reload]);

  // MapLibre live map with scan trail
  useEffect(() => {
    if (!mapContainerRef.current || !trail) return;
    const geoPoints = (trail.points || []).filter(p => p.lat != null && p.lng != null);
    if (geoPoints.length === 0) return;

    const IP_COLORS = {
      IQC_DESK: '#3B82F6', BIN_PUTAWAY: '#0EA5E9', RESERVATION: '#8B5CF6',
      ISSUE_COUNTER: '#F97316', WH_EXIT_GATE: '#F59E0B',
      SITE_ENTRY_GATE: '#10B981', STATION_ENTRY_GATE: '#059669',
      POINT_OF_USE: '#EF4444', INSTALLATION: '#16A34A',
      RETURN_PICKUP: '#94A3B8', SITE_EXIT_GATE: '#64748B',
      STATION_EXIT_GATE: '#64748B', WH_ENTRY_GATE: '#64748B',
      RECEIPT_COUNTER: '#334155',
    };
    // init once
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {
          sat: {
            type: 'raster', tileSize: 256,
            tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
            attribution: 'Esri World Imagery',
          },
        },
        layers: [{ id: 'sat', type: 'raster', source: 'sat' }],
      },
      center: [geoPoints[0].lng, geoPoints[0].lat],
      zoom: 15, attributionControl: false,
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: false }), 'top-right');
    mapRef.current = map;
    map.on('load', () => {
      // Fit bounds
      if (geoPoints.length > 1) {
        const b = new maplibregl.LngLatBounds();
        geoPoints.forEach(p => b.extend([p.lng, p.lat]));
        map.fitBounds(b, { padding: 40, maxZoom: 17.5, duration: 500 });
      }
      // Polyline
      map.addSource('trail-line', {
        type: 'geojson',
        data: { type: 'Feature', geometry:
          { type: 'LineString', coordinates: geoPoints.map(p => [p.lng, p.lat]) } },
      });
      map.addLayer({
        id: 'trail-line-glow', type: 'line', source: 'trail-line',
        paint: { 'line-color': '#F59E0B', 'line-opacity': 0.35, 'line-width': 8, 'line-blur': 6 },
      });
      map.addLayer({
        id: 'trail-line', type: 'line', source: 'trail-line',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#F97316', 'line-width': 2.5,
                  'line-dasharray': [1.5, 1.2] },
      });
      // Hotspot halos (dwell ≥ 4h)
      const hotspots = (trail.hotspots || []).filter(h => h.lat != null && h.lng != null);
      if (hotspots.length > 0) {
        map.addSource('hotspots', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: hotspots.map(h => ({
            type: 'Feature', properties: { dwell: h.dwellHours, type: h.interfaceType },
            geometry: { type: 'Point', coordinates: [h.lng, h.lat] },
          })) },
        });
        map.addLayer({
          id: 'hotspots-halo', type: 'circle', source: 'hotspots',
          paint: { 'circle-radius': 22, 'circle-color': '#EF4444',
                    'circle-opacity': 0.18, 'circle-blur': 0.5 },
        });
      }
      // Scan point circles
      map.addSource('trail-points', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: geoPoints.map((p, i) => ({
          type: 'Feature',
          properties: { i, type: p.interfaceType, isCurrent: p.isCurrent || false,
                        color: IP_COLORS[p.interfaceType] || '#64748B' },
          geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
        })) },
      });
      map.addLayer({
        id: 'trail-points', type: 'circle', source: 'trail-points',
        paint: {
          'circle-radius': ['case', ['get', 'isCurrent'], 9, 6],
          'circle-color': ['get', 'color'],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': ['case', ['get', 'isCurrent'], 3, 2],
        },
      });
      // popup on hover
      const popup = new maplibregl.Popup({ closeButton: false, offset: 10 });
      map.on('mouseenter', 'trail-points', (e) => {
        map.getCanvas().style.cursor = 'pointer';
        const f = e.features[0]; const p = f.properties;
        const pt = geoPoints[p.i];
        popup.setLngLat(f.geometry.coordinates).setHTML(
          `<div style="font-family:sans-serif;font-size:11px;padding:2px 4px">
             <b style="color:${p.color}">${pt.interfaceType?.replace(/_/g,' ')}</b><br>
             ${pt.scannedAt?.slice(0,16).replace('T',' ')}<br>
             by <b>${pt.scannedBy || '—'}</b>
             ${pt.dwellHours != null ? `<br>dwell <b>${pt.dwellHours}h</b>` : ''}
           </div>`
        ).addTo(map);
      });
      map.on('mouseleave', 'trail-points', () => {
        map.getCanvas().style.cursor = ''; popup.remove();
      });
    });
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [trail]);

  const gateBadge = (g, label) => (
    <span className="text-[9px] px-1 rounded" style={{
      background: g?.actualDate ? '#D1FAE5' : g?.forecastDate ? '#FEF3C7' : '#F1F5F9',
      color: g?.actualDate ? '#065F46' : g?.forecastDate ? '#92400E' : '#64748B',
    }}>{label}</span>
  );

  const kpis = trail?.kpis || {};
  const legendItems = Object.entries(trail?.legend || {})
    .sort((a, b) => b[1] - a[1]);
  const IP_COLORS = {
    IQC_DESK: '#3B82F6', BIN_PUTAWAY: '#0EA5E9', RESERVATION: '#8B5CF6',
    ISSUE_COUNTER: '#F97316', WH_EXIT_GATE: '#F59E0B',
    SITE_ENTRY_GATE: '#10B981', STATION_ENTRY_GATE: '#059669',
    POINT_OF_USE: '#EF4444', INSTALLATION: '#16A34A',
    RETURN_PICKUP: '#94A3B8', SITE_EXIT_GATE: '#64748B',
    STATION_EXIT_GATE: '#64748B', WH_ENTRY_GATE: '#64748B',
    RECEIPT_COUNTER: '#334155',
  };
  const hotspotList = (trail?.hotspots || []).slice(0, 5);
  const geoPointCount = (trail?.points || []).filter(p => p.lat != null && p.lng != null).length;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}
      data-testid="coc-drawer">
      <div className="bg-white w-full max-w-4xl h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-3 border-b sticky top-0 bg-white z-10" style={{ borderColor: C.border }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase font-semibold" style={{ color: C.accent }}>Live Chain of Custody</div>
              <div className="text-sm font-bold" style={{ color: C.slate }}>{row.materialUnitId}</div>
              <div className="text-[10px] font-mono" style={{ color: C.slateMuted }}>
                QR: {row.qrCode} · RFID: {row.rfidTag}
                {refreshedAt && (
                  <span className="ml-2 inline-flex items-center gap-0.5" style={{ color: C.success }}>
                    <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.success }} />
                    live · updated {refreshedAt.toLocaleTimeString()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={reload} disabled={loading}
                className="w-7 h-7 rounded border flex items-center justify-center"
                style={{ borderColor: C.border }}
                data-testid="coc-refresh">
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} style={{ color: C.primary }} />
              </button>
              <Button size="sm" onClick={() => onScanFromDrawer?.(row.qrCode || row.materialUnitId)}
                className="text-white text-[10px] h-7" style={{ background: C.primary }}
                data-testid="coc-scan-btn">
                <ScanLine className="w-3 h-3 mr-0.5" /> Scan
              </Button>
              <button onClick={onClose} data-testid="coc-close"><X className="w-4 h-4" style={{ color: C.slateMuted }} /></button>
            </div>
          </div>
        </div>

        {loading && !trail && <div className="p-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}

        {trail && (
          <div className="p-3 space-y-3">
            {/* Real-time KPI strip */}
            <div className="grid grid-cols-6 gap-1.5">
              {[
                { icon: Ruler, label: 'Distance', value: `${kpis.totalDistanceKm || 0} km`, color: C.primary },
                { icon: Timer, label: 'Time in Field', value: `${kpis.timeInFieldHours || 0} h`, color: C.info },
                { icon: Route, label: 'Scans', value: kpis.scanCount || 0, color: C.success },
                { icon: Users, label: 'Handovers', value: kpis.handovers || 0, color: C.accent },
                { icon: Flame, label: 'Hotspots ≥4h', value: kpis.hotspotCount || 0, color: C.danger },
                { icon: WifiOff, label: 'GPS Gaps', value: kpis.gpsGaps || 0, color: kpis.gpsGaps > 0 ? C.warn : C.slateMuted },
              ].map((k, i) => (
                <div key={i} className="p-1.5 rounded border bg-white text-center" style={{ borderColor: C.border }}
                  data-testid={`coc-kpi-${k.label.toLowerCase().replace(/[^a-z]/g,'-')}`}>
                  <k.icon className="w-3 h-3 mx-auto mb-0.5" style={{ color: k.color }} />
                  <div className="text-[8.5px] uppercase font-semibold" style={{ color: C.slateMuted }}>{k.label}</div>
                  <div className="text-[12px] font-bold font-mono" style={{ color: k.color }}>{k.value}</div>
                </div>
              ))}
            </div>

            {/* Live map with scan trail */}
            <div className="rounded-lg border overflow-hidden" style={{ borderColor: C.border }}
              data-testid="coc-live-map">
              <div className="px-2 py-1.5 border-b flex items-center gap-1.5" style={{ borderColor: C.border, background: '#F8FAFC' }}>
                <MapIcon className="w-3.5 h-3.5" style={{ color: C.primary }} />
                <div className="text-[10px] uppercase font-bold" style={{ color: C.primary }}>Real-time GPS Trail</div>
                {(trail.currentState) && (
                  <span className="ml-1 text-[9px] font-mono px-1 rounded"
                    style={{ background: C.primary, color: '#fff' }}>{trail.currentState.replace(/_/g, ' ')}</span>
                )}
                <span className="ml-auto text-[9px] font-mono" style={{ color: C.slateMuted }}>
                  {geoPointCount} geo-scans · {trail.hotspots?.length || 0} hotspots
                </span>
              </div>
              {geoPointCount === 0 ? (
                <div className="h-52 flex items-center justify-center text-[10px] italic" style={{ color: C.slateMuted, background: '#F8FAFC' }}>
                  <MapPin className="w-3 h-3 mr-1" /> No GPS coordinates captured yet · trail activates as scans fire on-site
                </div>
              ) : (
                <div ref={mapContainerRef} style={{ height: 320, width: '100%' }} />
              )}
              {/* Legend */}
              {legendItems.length > 0 && (
                <div className="px-2 py-1.5 border-t flex flex-wrap gap-1.5 text-[9px]" style={{ borderColor: C.border, background: '#F8FAFC' }}>
                  <span className="uppercase font-semibold" style={{ color: C.slateMuted }}>Legend:</span>
                  {legendItems.map(([type, count]) => (
                    <span key={type} className="inline-flex items-center gap-0.5 px-1 rounded"
                      style={{ background: '#fff', border: `1px solid ${C.border}` }}
                      data-testid={`coc-legend-${type}`}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999,
                                      background: IP_COLORS[type] || C.slateMuted }} />
                      <b style={{ color: C.slate }}>{type.replace(/_/g, ' ')}</b>
                      <span className="font-mono" style={{ color: C.slateMuted }}>×{count}</span>
                    </span>
                  ))}
                  {(trail.hotspots || []).length > 0 && (
                    <span className="inline-flex items-center gap-0.5 px-1 rounded"
                      style={{ background: '#FEE2E2', border: `1px solid #FCA5A5` }}>
                      <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 999,
                                      background: '#EF4444', opacity: 0.35 }} />
                      <b style={{ color: '#991B1B' }}>Hotspot halo · dwell ≥ 4h</b>
                    </span>
                  )}
                </div>
              )}
              {/* Hotspot detail list */}
              {hotspotList.length > 0 && (
                <div className="px-2 py-1.5 border-t space-y-0.5" style={{ borderColor: C.border }}>
                  <div className="text-[9px] uppercase font-semibold flex items-center gap-1" style={{ color: C.danger }}>
                    <Flame className="w-3 h-3" /> Dwell hotspots — investigate long stops
                  </div>
                  {hotspotList.map((h, i) => (
                    <div key={i} className="text-[9.5px] flex items-center gap-1" style={{ color: C.slate }}
                      data-testid={`coc-hotspot-${i}`}>
                      <ArrowRight className="w-2.5 h-2.5" style={{ color: C.danger }} />
                      <b>{h.interfaceType?.replace(/_/g, ' ')}</b>
                      <span style={{ color: C.slateMuted }}>· {h.scannedAt?.slice(0,16).replace('T',' ')}</span>
                      <span className="ml-auto font-mono font-bold" style={{ color: C.danger }}>{h.dwellHours} h</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upstream Procurement context — collapsed by default */}
            <div className="rounded-lg border" style={{ borderColor: C.border }}>
              <button className="w-full p-2 flex items-center justify-between"
                onClick={() => setExpandUpstream(!expandUpstream)}
                data-testid="coc-upstream-toggle">
                <div className="flex items-center gap-2">
                  {expandUpstream ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  <span className="text-[10px] uppercase font-bold" style={{ color: C.primary }}>Upstream Procurement Context</span>
                </div>
                <span className="text-[9px]" style={{ color: C.slateMuted }}>PO · Gates · GRN · IQC</span>
              </button>
              {expandUpstream && upstream && (
                <div className="p-2 border-t space-y-2" style={{ borderColor: C.border }}>
                  <div className="grid grid-cols-2 gap-2 text-[10px]">
                    <div><span style={{ color: C.slateMuted }}>MCI:</span> <b style={{ color: C.primary }}>{upstream.mci.mciLineNumber}</b></div>
                    <div><span style={{ color: C.slateMuted }}>Vendor:</span> <b>{upstream.mci.vendor}</b></div>
                    <div className="col-span-2"><span style={{ color: C.slateMuted }}>Material:</span> {upstream.mci.materialDescription}</div>
                    <div><span style={{ color: C.slateMuted }}>PO:</span> <b>{upstream.mci.poNumber} · L{upstream.mci.poLineItem || '—'}</b></div>
                    <div><span style={{ color: C.slateMuted }}>Location:</span> {upstream.mci.defaultReceiptLocation || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Pre-Order Gates</div>
                    <div className="flex flex-wrap gap-1">
                      {(upstream.preOrderGates || []).map(g => gateBadge(g, g.name))}
                      {(upstream.preOrderGates || []).length === 0 && <span className="text-[10px]" style={{ color: C.slateMuted }}>—</span>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Post-Order Gates</div>
                    <div className="flex flex-wrap gap-1">
                      {(upstream.postOrderGates || []).map(g => gateBadge(g, g.name))}
                      {(upstream.postOrderGates || []).length === 0 && <span className="text-[10px]" style={{ color: C.slateMuted }}>—</span>}
                    </div>
                  </div>
                  {upstream.receipt && (
                    <div className="p-2 rounded" style={{ background: '#F8FAFC' }}>
                      <div className="text-[9px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>GRN · IQC · {upstream.receipt.receiptNumber}</div>
                      <div className="grid grid-cols-4 gap-1 text-[10px]">
                        <div><span style={{ color: C.slateMuted }}>GRN:</span> <b>{upstream.receipt.grnQty}</b></div>
                        <div><span style={{ color: C.slateMuted }}>Pending:</span> <b style={{ color: C.warn }}>{upstream.receipt.iqcQty}</b></div>
                        <div><span style={{ color: C.slateMuted }}>Accepted:</span> <b style={{ color: C.success }}>{upstream.receipt.acceptedQty}</b></div>
                        <div><span style={{ color: C.slateMuted }}>Rejected:</span> <b style={{ color: C.danger }}>{upstream.receipt.rejectedQty}</b></div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 12-point stepper */}
            <div className="rounded-lg border p-2" style={{ borderColor: C.border }}>
              <div className="text-[10px] uppercase font-bold mb-2" style={{ color: C.primary }}>
                12-Point Chain of Custody · Red gaps = missing scans
              </div>
              <div className="space-y-1.5">
                {stepper.map((s, i) => {
                  const done = !!s.event;
                  return (
                    <div key={i} className="flex items-start gap-2"
                      data-testid={`coc-step-${s.interfaceType}`}>
                      <div className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0"
                        style={{ background: done ? C.success : C.danger, color: '#fff' }}>
                        {done ? <Check className="w-2.5 h-2.5" /> : '·'}
                      </div>
                      <div className="flex-1 pb-1.5" style={{ borderBottom: i < 11 ? `1px dashed ${C.border}` : 'none' }}>
                        <div className="flex items-center justify-between">
                          <div className="text-[10px] font-semibold" style={{ color: done ? C.slate : C.danger }}>
                            #{i+1} · {s.interfaceType.replace(/_/g, ' ')}
                          </div>
                          <div className="text-[9px]" style={{ color: C.slateMuted }}>
                            {done ? fmtTime(s.event.scannedAt) : 'Not scanned'}
                          </div>
                        </div>
                        {done && (
                          <div className="text-[9px] mt-0.5 space-y-0.5" style={{ color: C.slateMuted }}>
                            <div>
                              by <b style={{ color: C.slate }}>{s.event.scannedBy}</b>
                              {s.event.deviceId && <span> · device <b style={{ color: C.slate }}>{s.event.deviceId}</b></span>}
                            </div>
                            {(s.event.gpsLat != null && s.event.gpsLng != null) && (
                              <div>
                                <MapPin className="w-2.5 h-2.5 inline mr-0.5" style={{ color: C.primary }} />
                                GPS <span className="font-mono">{s.event.gpsLat.toFixed(4)}, {s.event.gpsLng.toFixed(4)}</span>
                              </div>
                            )}
                            {s.event.notes && (
                              <div className="italic" style={{ color: C.slate }}>“{s.event.notes}”</div>
                            )}
                            {(s.event.photoUrls || []).length > 0 && (
                              <div className="flex gap-1 mt-1">
                                {(s.event.photoUrls || []).slice(0, 4).map((u, idx) => (
                                  <a key={idx} href={u} target="_blank" rel="noreferrer"
                                    className="inline-block w-8 h-8 rounded border overflow-hidden"
                                    style={{ borderColor: C.border, background: '#F1F5F9' }}
                                    title={`Photo ${idx + 1}`}>
                                    <img src={u} alt="scan" className="w-full h-full object-cover" />
                                  </a>
                                ))}
                              </div>
                            )}
                            <div className="text-[8.5px] font-mono">
                              hash <span style={{ color: C.slate }}>{String(s.event.hashCurrent || '').slice(0, 10)}…</span>
                              {s.event.isOverride && <span className="ml-1 text-[9px] px-1 rounded" style={{ background: '#FEE2E2', color: '#991B1B' }}>OVERRIDE</span>}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Anomalies */}
            {row.anomalies && row.anomalies.length > 0 && (
              <div className="rounded-lg border p-2" style={{ borderColor: C.border, background: '#FEF3C7' }}>
                <div className="text-[10px] uppercase font-bold mb-1" style={{ color: '#92400E' }}>
                  <AlertTriangle className="w-3 h-3 inline mr-1" />Anomalies auto-detected
                </div>
                {row.anomalies.map((a, i) => (
                  <div key={i} className="text-[10px] mt-1">
                    <span className="px-1 rounded font-bold text-[9px]"
                      style={{ background: a.severity === 'HIGH' ? C.danger : C.warn, color: '#fff' }}>{a.code}</span>
                    <span className="ml-1" style={{ color: C.slate }}>{a.msg}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MapGroupView({ rows }) {
  // Simple grouped-by-warehouse layout so the "map" view is functional
  // pre-MapLibre (M6). One tile per state cluster.
  const clusters = useMemo(() => {
    const g = {};
    rows.forEach(r => {
      const k = r.state || 'UNKNOWN';
      if (!g[k]) g[k] = [];
      g[k].push(r);
    });
    return g;
  }, [rows]);
  return (
    <div className="p-2 grid grid-cols-3 gap-2" data-testid="ft-map-view"
      style={{ minHeight: 320, background: '#F8FAFC', border: `1px solid rgba(15,23,42,0.10)`, borderRadius: 8 }}>
      {Object.entries(clusters).map(([state, items]) => (
        <div key={state} className="p-2 rounded-lg border" style={{ borderColor: C.border, background: '#F8FAFC' }}>
          <div className="flex items-center gap-1 mb-1.5">
            <MapPin className="w-3 h-3" style={{ color: C.primary }} />
            <div className="text-[10px] uppercase font-bold" style={{ color: C.primary }}>{state.replace(/_/g, ' ')}</div>
            <span className="ml-auto text-[10px] font-mono font-bold" style={{ color: C.slate }}>{items.length}</span>
          </div>
          <div className="space-y-0.5 max-h-36 overflow-y-auto">
            {items.slice(0, 15).map(r => (
              <div key={r.materialUnitId} className="text-[9px] truncate"
                style={{ color: r.risk === 'HIGH' ? C.danger : r.risk === 'MEDIUM' ? C.warn : C.slate }}>
                <span className="font-mono">{r.mciLineNumber}</span> · {r.materialDescription?.slice(0, 30)}
              </div>
            ))}
            {items.length > 15 && <div className="text-[9px] italic" style={{ color: C.slateMuted }}>+{items.length - 15} more</div>}
          </div>
        </div>
      ))}
      {Object.keys(clusters).length === 0 && (
        <div className="col-span-3 p-8 text-center text-sm" style={{ color: C.slateMuted }}>
          No field activity yet. Approve a MIR in Field Issue to start.
        </div>
      )}
    </div>
  );
}

function CognaraPanel({ projectId, refreshKey }) {
  const [ins, setIns] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mtx/field-tracking/cognara-insights`);
        if (alive) setIns(data.insights || []);
      } catch { /* silent */ }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [projectId, refreshKey]);

  const sevColor = { HIGH: C.danger, MEDIUM: C.warn, INFO: C.info };
  return (
    <div className="sticky top-4 w-64 flex-shrink-0 rounded-lg border p-2"
      style={{ borderColor: C.border, background: 'linear-gradient(180deg, #FEF3C7 0%, #FFFFFF 100%)' }}
      data-testid="cognara-panel">
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="w-3.5 h-3.5" style={{ color: C.accent }} />
        <div className="text-[10px] uppercase font-bold" style={{ color: C.accent }}>Cognara · Field Insights</div>
      </div>
      {loading && <div className="text-[10px] text-center py-3" style={{ color: C.slateMuted }}>Analyzing…</div>}
      {!loading && ins.map((i, idx) => (
        <div key={idx} className="mb-2 p-2 rounded border bg-white" style={{ borderColor: C.border }}
          data-testid={`cognara-insight-${idx}`}>
          <div className="flex items-center gap-1 mb-1">
            <span className="px-1 rounded text-[8.5px] font-bold text-white" style={{ background: sevColor[i.severity] || C.slateMuted }}>{i.severity}</span>
            <div className="text-[10px] font-bold" style={{ color: C.slate }}>{i.title}</div>
          </div>
          <div className="text-[9px]" style={{ color: C.slateMuted }}>{i.detail}</div>
          <div className="text-[9px] mt-1 italic" style={{ color: C.primary }}>→ {i.action}</div>
        </div>
      ))}
    </div>
  );
}

export default function FieldTrackingTab({ projectId }) {
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [q, setQ] = useState('');
  const [riskFilter, setRiskFilter] = useState('ALL');
  const [drillRow, setDrillRow] = useState(null);
  const [scanSeed, setScanSeed] = useState(null); // { code } — opens Scan Center
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [r, k] = await Promise.all([
        api.get(`/v1/projects/${projectId}/mtx/field-tracking/register`),
        api.get(`/v1/projects/${projectId}/mtx/field-tracking/kpis`),
      ]);
      setRows(r.data.rows || []); setKpis(k.data);
    } catch { /* silent */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh every 30s so the register stays live during field ops
  useEffect(() => {
    if (!projectId) return;
    const t = setInterval(() => { load(); }, 30000);
    return () => clearInterval(t);
  }, [projectId, load]);

  const filtered = rows.filter(r => {
    if (riskFilter !== 'ALL' && r.risk !== riskFilter) return false;
    if (q) {
      const hay = `${r.mciLineNumber} ${r.materialCode || ''} ${r.materialDescription || ''} ${r.vendor || ''} ${r.state} ${r.lastScanBy || ''}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div className="p-3 flex gap-3" data-testid="field-tracking-tab">
      <div className="flex-1 min-w-0 space-y-3">
        {/* 6 KPI tiles */}
        <div className="grid grid-cols-6 gap-2">
          <KpiTile label="Currently in Field" value={kpis.currentlyInField}
            subValue={kpis.currentlyInFieldValue ? fmtValue(kpis.currentlyInFieldValue, kpis.currency) : null}
            color={C.primary} testId="ft-kpi-field" />
          <KpiTile label="Missed-Scan Alerts" value={kpis.missedScanAlerts} color={C.warn} testId="ft-kpi-missed" />
          <KpiTile label="Awaiting Install >24h" value={kpis.awaitingInstall} color={C.danger} testId="ft-kpi-install" />
          <KpiTile label="Overdue Return" value={kpis.overdueReturn} color={C.warn} testId="ft-kpi-return" />
          <KpiTile label="Unaccounted >7d" value={kpis.unaccounted} color={C.danger} testId="ft-kpi-unaccounted" />
          <KpiTile label="Wastage This Month" value={kpis.wastageThisMonth} color={C.danger} testId="ft-kpi-wastage" />
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search material / MCI / vendor / state…"
            className="px-2 py-1 rounded border text-xs flex-1 min-w-[220px]" style={{ borderColor: C.border }}
            data-testid="ft-search" />
          <div className="flex rounded border overflow-hidden" style={{ borderColor: C.border }}>
            {['ALL', 'HIGH', 'MEDIUM', 'LOW'].map(r => (
              <button key={r} onClick={() => setRiskFilter(r)}
                className="px-2 py-1 text-[10px] font-semibold"
                data-testid={`ft-risk-${r}`}
                style={{
                  background: riskFilter === r ? (r === 'ALL' ? C.primary : RISK_COLORS[r]) : '#fff',
                  color: riskFilter === r ? '#fff' : C.slate,
                }}>{r}</button>
            ))}
          </div>
          <div className="flex rounded border overflow-hidden" style={{ borderColor: C.border }}>
            <button onClick={() => setView('list')} className="px-2 py-1 text-[10px] font-semibold"
              data-testid="ft-view-list"
              style={{ background: view === 'list' ? C.primary : '#fff', color: view === 'list' ? '#fff' : C.slate }}>
              <List className="w-3 h-3 inline mr-0.5" /> List
            </button>
            <button onClick={() => setView('map')} className="px-2 py-1 text-[10px] font-semibold"
              data-testid="ft-view-map"
              style={{ background: view === 'map' ? C.primary : '#fff', color: view === 'map' ? '#fff' : C.slate }}>
              <MapIcon className="w-3 h-3 inline mr-0.5" /> Map
            </button>
          </div>
          <Button size="sm" variant="ghost" onClick={() => { load(); setRefreshKey(k => k + 1); }}
            data-testid="ft-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" onClick={() => setScanSeed({ code: '' })}
            className="text-white font-semibold" style={{ background: C.primary }}
            data-testid="ft-scan-center-btn">
            <ScanLine className="w-3.5 h-3.5 mr-1" /> Scan Center
          </Button>
          <div className="text-[10px]" style={{ color: C.slateMuted }}>{filtered.length} units</div>
        </div>

        {loading && <div className="p-6 text-center text-sm" style={{ color: C.slateMuted }}>Loading register…</div>}

        {!loading && view === 'list' && (
          <div className="rounded-lg border overflow-hidden" style={{ borderColor: C.border }}>
            <table className="w-full text-[11px]">
              <thead style={{ background: '#F8FAFC' }}>
                <tr>
                  {['Material', 'MCI', 'Batch / GRN', 'Qty', 'Value', 'State', 'Last Scan Point', 'Time', 'GPS', 'Days in State', 'Assigned To', 'Risk'].map(h => (
                    <th key={h} className="text-left p-1.5 uppercase text-[9px] font-semibold" style={{ color: C.slateMuted }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={12} className="p-6 text-center text-[10px]" style={{ color: C.slateMuted }}>
                    <Package className="w-4 h-4 inline mb-1" /><br />No units in field. Approve a MIR to start.
                  </td></tr>
                )}
                {filtered.map(r => (
                  <tr key={r.materialUnitId} className="hover:bg-blue-50 cursor-pointer"
                    style={{ borderTop: `1px solid ${C.border}` }}
                    onClick={() => setDrillRow(r)}
                    data-testid={`ft-row-${r.materialUnitId}`}>
                    <td className="p-1.5">
                      <div className="text-[11px] max-w-[220px] truncate" style={{ color: C.slate }}>{r.materialDescription}</div>
                      <div className="text-[9px] font-mono" style={{ color: C.slateMuted }}>{r.materialCode}</div>
                    </td>
                    <td className="p-1.5 font-mono text-[10px] font-bold" style={{ color: C.primary }}>{r.mciLineNumber}</td>
                    <td className="p-1.5 font-mono text-[10px]" style={{ color: C.accent }}>
                      {r.batchNumber && <div className="text-[9px]" style={{ color: C.slateMuted }}>B: {r.batchNumber}</div>}
                      <div>{r.receiptNumber}</div>
                    </td>
                    <td className="p-1.5 font-mono text-[10px]" style={{ color: C.slate }}>{r.quantity} <span style={{ color: C.slateMuted }}>{r.unit}</span></td>
                    <td className="p-1.5 font-mono text-[10px] font-semibold" style={{ color: C.success }}>{fmtValue(r.value, r.currency)}</td>
                    <td className="p-1.5"><span className="px-1 rounded text-[9px] font-bold text-white"
                      style={{ background: C.primary }}>{r.state.replace(/_/g, ' ')}</span></td>
                    <td className="p-1.5 text-[10px]" style={{ color: C.slate }}>{r.lastScanPoint?.replace(/_/g, ' ') || '—'}</td>
                    <td className="p-1.5 text-[10px]" style={{ color: C.slateMuted }}>{fmtTime(r.lastScanAt)}</td>
                    <td className="p-1.5 text-[10px]" style={{ color: C.slateMuted }}>
                      {(r.gpsLat && r.gpsLng) ? `${r.gpsLat.toFixed(3)}, ${r.gpsLng.toFixed(3)}` : '—'}
                    </td>
                    <td className="p-1.5 text-[10px] font-mono" style={{ color: r.daysInState > 3 ? C.danger : C.slate }}>{r.daysInState ?? '—'}</td>
                    <td className="p-1.5 text-[10px]" style={{ color: C.slate }}>{r.lastScanBy || '—'}</td>
                    <td className="p-1.5">
                      <span className="px-1 rounded text-[9px] font-bold text-white"
                        style={{ background: RISK_COLORS[r.risk] }}
                        data-testid={`ft-risk-badge-${r.materialUnitId}`}>{r.risk}</span>
                      {r.anomalies?.length > 0 && (
                        <AlertTriangle className="w-3 h-3 inline ml-1" style={{ color: C.warn }} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && view === 'map' && <MapGroupView rows={filtered} />}
      </div>

      <CognaraPanel projectId={projectId} refreshKey={refreshKey} />

      {drillRow && <ChainOfCustodyDrawer projectId={projectId} row={drillRow}
        onClose={() => setDrillRow(null)}
        onScanFromDrawer={(code) => { setDrillRow(null); setScanSeed({ code }); }} />}
      {scanSeed && <ScanCenterDialog projectId={projectId} seedCode={scanSeed.code}
        onClose={() => setScanSeed(null)}
        onScanned={() => { load(); setRefreshKey(k => k + 1); }} />}
    </div>
  );
}
